import { useState, useEffect, useRef, useCallback } from 'react'
import { scratchpad as scratchpadApi } from '../lib/api'

interface ScratchpadProps {
  ideaId: string
}

export default function Scratchpad({ ideaId }: ScratchpadProps) {
  const [content, setContent] = useState('')
  const [savedContent, setSavedContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const saveTimeoutRef = useRef<number | null>(null)

  // Load scratchpad content
  useEffect(() => {
    let cancelled = false
    scratchpadApi
      .get(ideaId)
      .then((data) => {
        if (!cancelled) {
          const text = data?.content || ''
          setContent(text)
          setSavedContent(text)
        }
      })
      .catch(() => {
        // Scratchpad may not exist yet
      })
    return () => {
      cancelled = true
    }
  }, [ideaId])

  // Auto-save with debounce
  const save = useCallback(
    async (text: string) => {
      if (text === savedContent) return
      setSaving(true)
      try {
        await scratchpadApi.save(ideaId, text)
        setSavedContent(text)
        setLastSaved(new Date())
      } catch (e) {
        console.error('Failed to save scratchpad:', e)
      } finally {
        setSaving(false)
      }
    },
    [ideaId, savedContent]
  )

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value
    setContent(newContent)

    // Debounce auto-save (1.5s after last keystroke)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    saveTimeoutRef.current = window.setTimeout(() => {
      save(newContent)
    }, 1500)
  }

  // Save on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  const isDirty = content !== savedContent

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-yellow-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-300">Scratchpad</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {saving && <span className="text-yellow-400">Saving...</span>}
          {!saving && isDirty && <span className="text-orange-400">Unsaved</span>}
          {!saving && !isDirty && lastSaved && (
            <span className="text-gray-600">Saved</span>
          )}
        </div>
      </div>

      {/* Editor */}
      <textarea
        value={content}
        onChange={handleChange}
        placeholder="Jot down notes, ideas, questions...&#10;&#10;This is your private scratchpad for this idea. Content auto-saves."
        className="flex-1 w-full bg-transparent text-gray-200 text-sm p-3 resize-none outline-none placeholder-gray-600 leading-relaxed"
        spellCheck={false}
      />
    </div>
  )
}
